import { GridToolbarContainer } from '@mui/x-data-grid-pro';
import { getUserPermission, UserKeys } from '../../../../../../api/users-api';
import { Doc, Folder, SharedDriveModel } from '../../../../../../../types';
import { Dispatch, SetStateAction } from 'react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CreateNewFolder from '@mui/icons-material/CreateNewFolder';
import DriveFileMove from '@mui/icons-material/DriveFileMove';
import Delete from '@mui/icons-material/Delete';
import Refresh from '@mui/icons-material/Refresh';
import Share from '@mui/icons-material/Share';

interface SharedDrivesChildGridToolbarProps {
  userEmail: string;
  selectedFolder: string;
  selectedDocs: Doc[] | null;
  setSelectedFolder: (value: SetStateAction<string>) => void;
  setOpenCreateFolderDialog: (value: SetStateAction<boolean>) => void;
  setOpenMoveDialog: (value: SetStateAction<boolean>) => void;
  setOpenTrashDialog: (value: SetStateAction<boolean>) => void;
  setOpenAddShareDialog: (value: SetStateAction<boolean>) => void;
  driveRoles: string[];
  sharedDrives: SharedDriveModel[];
  setOpenPermissionCheckDialog: (value: SetStateAction<boolean>) => void;
  setAttemptedAction: Dispatch<
    SetStateAction<
      | 'createFolder'
      | 'moveDocument'
      | 'trashDocument'
      | 'renameDocument'
      | 'editShare'
    >
  >;
}

export default function SharedDrivesChildGridToolbar({
  userEmail,
  selectedFolder,
  selectedDocs,
  setSelectedFolder,
  setOpenCreateFolderDialog,
  setOpenMoveDialog,
  setOpenTrashDialog,
  setOpenAddShareDialog,
  driveRoles,
  sharedDrives,
  setOpenPermissionCheckDialog,
  setAttemptedAction,
}: SharedDrivesChildGridToolbarProps) {
  const queryClient = useQueryClient();

  const isASharedDrive =
    sharedDrives.filter((drive) => drive.id === selectedFolder).length > 0;

  function createBreadcrumbs() {
    const breadcrumbs = [];

    const allFolders = queryClient.getQueryData<
      InfiniteData<Folder[], unknown> | undefined
    >([UserKeys.SharedDriveFolders, userEmail]);

    const folders = allFolders?.pages.reduce(
      (acc, page) => acc.concat(page),
      []
    );
    interface Node {
      id: string;
      title: string;
      iconUrl: string;
      parentId: string;
      children: Node[];
    }

    const folderNodes: Node[] =
      folders?.map((folder) => {
        return {
          id: folder.id,
          title: folder.title,
          iconUrl: 'folder',
          parentId: folder.parentId,
          children: [],
        };
      }) || [];

    const sharedDrivesFolders = sharedDrives.map((drive) => {
      return {
        id: drive.id,
        title: drive.name,
        iconUrl: 'drive',
        parentId: 'shared-drives',
        children: [],
      };
    });

    const uniqueFolders = folderNodes
      ?.concat(sharedDrivesFolders)
      .filter(
        (value, index, self) =>
          self.findIndex((v) => v.id === value.id) === index
      );

    const currentFolder = uniqueFolders?.find(
      (folder) => folder.id === selectedFolder
    );

    if (currentFolder) {
      breadcrumbs.push(
        <Typography
          key={currentFolder.id}
          onClick={() => setSelectedFolder(currentFolder.id)}
          variant="body2"
          sx={{
            ':hover': {
              cursor: 'pointer',
              color: 'ButtonText',
              backgroundColor: (theme) => theme.palette.primary.light,
              borderRadius: 2,
              px: 0.5,
            },
            px: 0.5,
          }}
        >
          {currentFolder.title}
        </Typography>
      );
    }

    if (currentFolder?.parentId) {
      const parentFolder = uniqueFolders?.find(
        (folder) => folder.id === currentFolder.parentId
      );

      if (parentFolder) {
        addParentBreadcrumb(parentFolder);
      }
    }

    function addParentBreadcrumb(parentFolder: Node) {
      breadcrumbs.push(
        <Typography
          key={parentFolder.id}
          onClick={() => setSelectedFolder(parentFolder.id)}
          variant="body2"
          sx={{
            ':hover': {
              cursor: 'pointer',
              color: 'ButtonText',
              backgroundColor: (theme) => theme.palette.primary.light,
              borderRadius: 2,
              px: 0.5,
            },
            px: 0.5,
          }}
        >
          {parentFolder.title}
        </Typography>
      );

      if (parentFolder.parentId) {
        const nextParentFolder = uniqueFolders?.find(
          (folder) => folder.id === parentFolder.parentId
        );

        if (nextParentFolder) {
          addParentBreadcrumb(nextParentFolder);
        }
      }
    }

    breadcrumbs.reverse();

    breadcrumbs.unshift(
      <Typography
        key={'shared-drives'}
        onClick={() => setSelectedFolder('shared-drives')}
        variant="body2"
        sx={{
          ':hover': {
            cursor: 'pointer',
            color: 'ButtonText',
            backgroundColor: (theme) => theme.palette.primary.light,
            borderRadius: 2,
            px: 0.5,
          },
          px: 0.5,
        }}
      >
        Shared Drives
      </Typography>
    );

    return breadcrumbs;
  }

  return (
    <GridToolbarContainer
      sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}
    >
      <Box>
        <Breadcrumbs maxItems={4} separator="›" sx={{ maxWidth: 500 }}>
          {createBreadcrumbs()}
        </Breadcrumbs>
      </Box>

      <Stack flexDirection="row">
        <Tooltip arrow title="Add Share">
          <span>
            <IconButton
              onClick={() => {
                setOpenAddShareDialog(true);
              }}
              disabled={
                !selectedDocs?.length ||
                !isAbleToSeeUIElement(driveRoles, DriveRoles.CanShareDocs.value)
              }
            >
              <Share />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          arrow
          title={
            !isAbleToSeeUIElement(driveRoles, DriveRoles.CanCreateFolders.value)
              ? Messages.Errors.Permission.Acting(
                  DriveRoles.CanCreateFolders.value
                )
              : 'Create Folder'
          }
        >
          <span>
            <IconButton
              onClick={async () => {
                const hasPermission = await queryClient.fetchQuery({
                  queryKey: [
                    UserKeys.SharedDriveUserPermission,
                    userEmail,
                    selectedFolder,
                  ],
                  queryFn: () =>
                    getUserPermission(
                      selectedFolder,
                      userEmail,
                      isASharedDrive
                    ),
                  retry: false,
                  staleTime: 1000 * 60 * 2,
                });

                const allowedPermissions = [
                  'writer',
                  'fileOrganizer',
                  'organizer',
                ];

                const hasCorrectPermission =
                  hasPermission && allowedPermissions.includes(hasPermission);

                if (hasCorrectPermission) {
                  setOpenCreateFolderDialog(true);
                } else if (!hasCorrectPermission) {
                  setOpenPermissionCheckDialog(true);
                  setAttemptedAction('createFolder');
                }
              }}
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanCreateFolders.value
                )
              }
            >
              <CreateNewFolder />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          arrow
          title={
            !isAbleToSeeUIElement(driveRoles, DriveRoles.CanMoveDocs.value)
              ? Messages.Errors.Permission.Acting(DriveRoles.CanMoveDocs.value)
              : 'Move'
          }
        >
          <span>
            <IconButton
              onClick={async () => {
                const hasPermission = await queryClient.fetchQuery({
                  queryKey: [
                    UserKeys.SharedDriveUserPermission,
                    userEmail,
                    selectedFolder,
                  ],
                  queryFn: () =>
                    getUserPermission(
                      selectedFolder,
                      userEmail,
                      isASharedDrive
                    ),
                  retry: false,
                  staleTime: 1000 * 60 * 2,
                });

                const allowedPermissions = ['organizer'];

                const hasCorrectPermission =
                  hasPermission && allowedPermissions.includes(hasPermission);

                if (hasCorrectPermission) {
                  setOpenMoveDialog(true);
                } else if (!hasCorrectPermission) {
                  setOpenPermissionCheckDialog(true);
                  setAttemptedAction('moveDocument');
                }
              }}
              disabled={
                !selectedDocs ||
                !isAbleToSeeUIElement(driveRoles, DriveRoles.CanMoveDocs.value)
              }
            >
              <DriveFileMove />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          arrow
          title={
            !isAbleToSeeUIElement(driveRoles, DriveRoles.CanTrashDocs.value)
              ? Messages.Errors.Permission.Acting(DriveRoles.CanTrashDocs.value)
              : 'Move to Trash'
          }
        >
          <span>
            <IconButton
              onClick={async () => {
                const hasPermission = await queryClient.fetchQuery({
                  queryKey: [
                    UserKeys.SharedDriveUserPermission,
                    userEmail,
                    selectedFolder,
                  ],
                  queryFn: () =>
                    getUserPermission(
                      selectedFolder,
                      userEmail,
                      isASharedDrive
                    ),
                  retry: false,
                  staleTime: 1000 * 60 * 2,
                });

                const allowedPermissions = ['fileOrganizer', 'organizer'];

                const hasCorrectPermission =
                  hasPermission && allowedPermissions.includes(hasPermission);

                if (hasCorrectPermission) {
                  setOpenTrashDialog(true);
                } else if (!hasCorrectPermission) {
                  setOpenPermissionCheckDialog(true);
                  setAttemptedAction('trashDocument');
                }
              }}
              disabled={
                !selectedDocs ||
                !isAbleToSeeUIElement(driveRoles, DriveRoles.CanTrashDocs.value)
              }
            >
              <Delete />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip arrow title="Refresh">
          <IconButton
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveDocuments],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveFolders],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DrivePermissions],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveAcls],
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDrivesForDomain],
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDrivesForUser],
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDriveFolders],
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDriveDocuments],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDriveUserPermission],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveLabels],
              });
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>
    </GridToolbarContainer>
  );
}
